import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkle, ShieldStar, Clock } from "@phosphor-icons/react";
import Logo from "../components/Logo";
import { Button } from "../components/ui/Button";
import { DEMO_CASES } from "../demo/cases";
import { setActiveDemoCase, clearDemoMode } from "../demo/demoInterceptor";

/**
 * Iter 20.08 — DemoLandingPage
 *
 * Öffentliche Route `/demo`. Zeigt zwei Case-Karten (Sarah + Julia) und
 * setzt beim Klick den Session-Marker, der den Demo-Modus aktiviert.
 */
export default function DemoLandingPage() {
  const navigate = useNavigate();

  // Beim Betreten der Landing-Seite: kein aktiver Case (User soll wählen).
  // Verhindert, dass ein alter Session-Marker EinstufungToolPage kontaminiert
  // falls User über die URL direkt hierher navigiert.
  useEffect(() => {
    clearDemoMode();
  }, []);

  const startCase = (caseId) => {
    setActiveDemoCase(caseId);
    // Force-refresh, damit AuthContext den Demo-User frisch lädt (statt
    // eines gecachten null-Users). Rechnung: sessionStorage ist tab-scoped
    // und AuthProvider läuft nur einmal beim App-Mount durch fetchMe().
    window.location.href = `/demo/${caseId}`;
  };

  return (
    <div
      className="min-h-screen bg-ink-50 flex flex-col"
      data-testid="demo-landing"
    >
      {/* Simple Top-Bar */}
      <div className="border-b border-ink-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex transition-crisp hover:opacity-80"
            data-testid="demo-landing-logo"
          >
            <Logo size="md" />
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="text-[12px] text-warm-600 hover:text-ink-900 transition-crisp"
              data-testid="demo-landing-home"
            >
              Zur Startseite
            </button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate("/login")}
              data-testid="demo-landing-login"
            >
              Anmelden
            </Button>
          </div>
        </div>
      </div>

      {/* Hero-Sektion */}
      <div className="border-b border-ink-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-500 mb-3">
            Live-Demo &middot; ohne Anmeldung
          </div>
          <h1 className="font-cabinet text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink-900 font-medium leading-tight max-w-3xl">
            Zwei Klicks, zwei Fälle — das Einstufungs-Tool in Aktion.
          </h1>
          <p className="mt-5 text-warm-700 max-w-2xl leading-relaxed">
            Wählen Sie einen der beiden Beispielfälle. Sie erleben den gesamten
            Ablauf so, wie ihn Ihre HR-Kolleginnen und Kollegen in der
            Vollversion durchführen — mit echten Interaktionen und ohne
            Anmeldung.
          </p>
        </div>
      </div>

      {/* Case-Karten */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 lg:px-10 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {["sarah", "julia"].map((caseId) => {
            const kase = DEMO_CASES[caseId];
            if (!kase) return null;
            const isSimple = caseId === "sarah";
            return (
              <button
                key={caseId}
                type="button"
                onClick={() => startCase(caseId)}
                data-testid={`demo-case-card-${caseId}`}
                className={[
                  "group relative text-left rounded-sm bg-white border-2 p-7 transition-crisp",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400",
                  isSimple
                    ? "border-emerald-300 hover:border-emerald-500 hover:shadow-[0_8px_32px_-16px_rgba(16,185,129,0.4)]"
                    : "border-amber-300 hover:border-amber-500 hover:shadow-[0_8px_32px_-16px_rgba(245,158,11,0.4)]",
                ].join(" ")}
              >
                {/* Top badge */}
                <div className="flex items-center justify-between mb-4">
                  <span
                    className={[
                      "inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider border",
                      isSimple
                        ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                        : "border-amber-300 bg-amber-50 text-amber-900",
                    ].join(" ")}
                  >
                    {isSimple ? (
                      <>
                        <Sparkle size={11} weight="bold" />
                        Standard-Fall
                      </>
                    ) : (
                      <>
                        <ShieldStar size={11} weight="bold" />
                        Komplex-Fall
                      </>
                    )}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] text-warm-500">
                    <Clock size={11} weight="bold" />
                    {kase.minutes}
                  </span>
                </div>

                {/* Titel */}
                <h2 className="font-cabinet text-2xl sm:text-3xl tracking-tight text-ink-900 font-medium leading-tight">
                  {kase.title}
                </h2>
                <div className="mt-1 text-[13px] text-warm-600 font-medium">
                  {kase.subtitle}
                </div>

                {/* Beschreibung */}
                <p className="mt-4 text-sm text-ink-800 leading-relaxed">
                  {kase.description}
                </p>

                {/* Details (mini feature list) */}
                <ul className="mt-5 space-y-1.5 text-[13px] text-warm-700">
                  {isSimple ? (
                    <>
                      <li>&bull; KV Handel — Angestellte</li>
                      <li>&bull; 2 Vordienstzeiten (Verkäuferin)</li>
                      <li>&bull; Ergebnis: Gruppe B, Stufe 2</li>
                    </>
                  ) : (
                    <>
                      <li>&bull; KV Metallgewerbe — Angestellte</li>
                      <li>&bull; 6 Vordienstzeiten inkl. Praktikum &amp; Selbständigkeit</li>
                      <li>&bull; Höchstanrechnung 12 Verwendungsgruppenjahre</li>
                      <li>&bull; Ergebnis: Gruppe E, Stufe 6</li>
                    </>
                  )}
                </ul>

                {/* CTA */}
                <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ink-900 group-hover:gap-3 transition-all">
                  Demo starten
                  <ArrowRight size={14} weight="bold" />
                </div>
              </button>
            );
          })}
        </div>

        {/* Legal / Hinweis */}
        <div className="mt-10 text-[11px] text-warm-500 max-w-2xl leading-relaxed">
          Alle Daten in dieser Demo sind Beispieldaten aus fiktiven Bewerbungsverfahren.
          Die Latenzen sind an die Vollversion angeglichen, damit die Demo authentisch wirkt.
        </div>
      </main>

      {/* Bottom-CTA */}
      <footer className="border-t border-ink-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="text-xs text-warm-600 leading-relaxed max-w-lg">
            <strong className="text-ink-900">Fragen zur Demo?</strong>{" "}
            Kontaktieren Sie uns über <a
              href="mailto:office@vorlagenportal.at"
              className="text-brand-600 underline underline-offset-2 hover:text-brand-700"
            >
              office@vorlagenportal.at
            </a>.
          </div>
          <Button
            variant="brand"
            size="md"
            onClick={() => navigate("/register")}
            data-testid="demo-landing-register"
          >
            Jetzt kostenlos testen
            <ArrowRight size={12} weight="bold" />
          </Button>
        </div>
      </footer>
    </div>
  );
}
